import * as React from 'react';
import {designerTheme, styled, emotionStyled} from '@packages/utilities/styled';
import BaseButton from '@packages/systems/design-system/Button';
import {WarningXlarge} from '@packages/systems/ui/icons';

import {
  ModalBody,
  ModalHeader,
  Modal as SpringModal,
  Text,
} from '@packages/systems/spring';

export type IconType = 'warning';
export type Size = 'small' | 'large';
export type BaseButtonType = {
  intent: 'primary' | 'danger' | 'emphasis' | 'default';
  label: string | React.ReactNode;
  ['data-automation-id']?: string;
};

export type ButtonType = BaseButtonType & {
  disabled?: boolean;
  ['data-automation-id']?: string;
};

const MIN_CONTENT_HEIGHT = 64;

const DialogButton = styled(
  React.forwardRef<HTMLElement, any>((props, ref) => (
    <BaseButton sizing="large" ref={ref} {...props} />
  ))
)(({theme: {control, fontSizes}}) => ({
  fontSize: fontSizes.copy,
  height: control.heightLarge,
}));

export const SubmitButton = styled(DialogButton)(({disabled}) => ({
  cursor: disabled ? 'not-allowed' : 'default',
  gridColumn: 3,
  justifySelf: 'end',
}));

export const SecondaryButton = emotionStyled(DialogButton)({
  gridColumn: 1,
  justifySelf: 'start',
});

export const CancelButton = emotionStyled(DialogButton)({
  gridColumn: 2,
  justifySelf: 'end',
});

const Footer = emotionStyled('div')({
  display: 'grid',
  gridTemplateColumns: '1fr auto auto',
  gridColumnGap: '$2',
  gridArea: 'buttons',
  gridAutoFlow: 'column',
});

const FooterContent = emotionStyled('div')({
  alignSelf: 'center',
});

const GridWrapper = emotionStyled('div')({
  display: 'grid',
  gridRowGap: '$2',
  gridTemplateRows: `minmax(${MIN_CONTENT_HEIGHT}px, auto) auto`,
  gridTemplateAreas: `
    "body"
    "buttons"
  `,
});

const Body = emotionStyled('div')({
  display: 'grid',
  gridColumnGap: '$2',
  gridTemplateColumns: `$7 1fr`,
  gridArea: 'body',
});

const Content = emotionStyled(Text)(
  {
    display: 'block',
    gridColumn: '1 / -1',
  },
  {
    variants: {
      hasIcon: {
        true: {
          gridColumn: '2 / 3',
          marginLeft: '$4',
          padding: '$4',
        },
      },
    },
  }
);

const iconStyle = {
  gridColumnStart: 1,
  margin: designerTheme.gutters.x16,
} as const;

const ICONS: Partial<
  Record<
    IconType,
    React.ComponentType<{
      style?: React.CSSProperties;
    }>
  >
> = {
  warning: WarningXlarge,
};

const SIZE = {
  small: 480,
  large: 640,
} as const;

type DialogProps = {
  title: string | React.ReactElement;
  titleDataAutomationId?: string;
  onClose: () => void;
  onCloseComplete?: () => void;
  content: React.ReactNode;
  contentStyle?: React.CSSProperties;
  footerContent?: React.ReactNode;
  buttons: React.ReactNode;
  iconType?: IconType;
  size?: Size;
  allowCancel?: boolean;
  ['data-automation-id']?: string;
  ['data-wf-modal-id']?: string;
  style?: React.CSSProperties;
};

export const Dialog = ({
  title,
  titleDataAutomationId,
  onClose,
  onCloseComplete,
  content,
  contentStyle,
  footerContent,
  buttons,
  iconType,
  size = 'small',
  allowCancel = true,
  ...props
}: DialogProps) => {
  const Icon = iconType ? ICONS[iconType] : null;

  const titleContent = title ? (
    typeof title === 'string' ? (
      <ModalHeader
        title={title}
        data-automation-id={titleDataAutomationId || 'modal-title'}
      />
    ) : (
      title
    )
  ) : null;

  return (
    <SpringModal
      isCentered
      {...props}
      isOpen
      data-automation-id={props['data-automation-id'] || 'modal-wrapper'}
      data-wf-modal-id={props['data-wf-modal-id']}
      hasCloseButton={allowCancel}
      onClose={onClose}
      onCloseComplete={onCloseComplete}
      width={SIZE[size]}
      shouldDisableAnimations
    >
      {titleContent}
      <ModalBody style={contentStyle}>
        <GridWrapper>
          <Body data-automation-id="dialog-body-content">
            {Icon && <Icon style={iconStyle} />}
            <Content hasIcon={Icon != null}>{content}</Content>
          </Body>

          <Footer>
            {footerContent && <FooterContent>{footerContent}</FooterContent>}
            {buttons}
          </Footer>
        </GridWrapper>
      </ModalBody>
    </SpringModal>
  );
};
